import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ScanSearch, Clock, AlertCircle, Sparkles, UserCog } from 'lucide-react';
import instagramAnalysesApi from 'api/requests/instagramAnalyses';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import Tabs from 'components/_UI/Tabs';
import Text from 'components/_UI/Text';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import ConfirmDialog from 'modules/restaurant/menu/components/ConfirmDialog';
import paths from 'routes/paths';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { Card, Muted } from './parts';
import { TABS, formatDate } from './labels';
import OverviewTab from './tabs/OverviewTab';
import ProfileTab from './tabs/ProfileTab';
import MarketTab from './tabs/MarketTab';
import AudienceTab from './tabs/AudienceTab';
import SalesTab from './tabs/SalesTab';
import PlanTab from './tabs/PlanTab';
import HistoryTab from './tabs/HistoryTab';

// Motivos em que o produtor resolve no perfil de produtor, não aqui.
const PROFILE_REASONS = ['missing_producer_profile', 'missing_instagram'];

export default function InstagramAnalysisPage() {
    const navigate = useNavigate();
    const { toast, toasts } = useToast();

    const [eligibility, setEligibility] = useState(null);
    const [analyses, setAnalyses] = useState([]);
    const [loadingList, setLoadingList] = useState(true);

    const [selectedId, setSelectedId] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);

    const [tab, setTab] = useState(TABS[0].name);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [requesting, setRequesting] = useState(false);

    useEffect(() => {
        fetchList();
    }, []);

    useEffect(() => {
        if (selectedId) {
            fetchAnalysis(selectedId);
        }
    }, [selectedId]);

    async function fetchList() {
        setLoadingList(true);

        try {
            const { data } = await instagramAnalysesApi.list();
            setEligibility(data.eligibility);
            setAnalyses(data.data || []);

            const latestCompleted = (data.data || []).find(item => item.status === 'completed');
            setSelectedId(current => current || latestCompleted?.id || null);
        } catch (error) {
            toast.error('Não foi possível carregar suas análises.', { position: 'top-center' });
        } finally {
            setLoadingList(false);
        }
    }

    async function fetchAnalysis(id) {
        setLoadingAnalysis(true);

        try {
            const { data } = await instagramAnalysesApi.get(id);
            setAnalysis(data.data);
        } catch (error) {
            toast.error('Não foi possível abrir esta análise.', { position: 'top-center' });
        } finally {
            setLoadingAnalysis(false);
        }
    }

    async function requestAnalysis() {
        setRequesting(true);

        try {
            await instagramAnalysesApi.request();
            toast.success('Pedido enviado! Avisamos por e-mail quando a análise ficar pronta.', { position: 'top-center' });
            await fetchList();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Não foi possível pedir a análise.', { position: 'top-center' });
        } finally {
            setRequesting(false);
            setConfirmOpen(false);
        }
    }

    function selectFromHistory(id) {
        setSelectedId(id);
        setTab(TABS[0].name);
    }

    const pillarsByTab = useMemo(() => {
        const pillars = analysis?.result?.pillars || [];
        return (tabName) => pillars.filter(pillar => pillar.tab === tabName);
    }, [analysis]);

    if (loadingList) {
        return <Centered><Loader /></Centered>;
    }

    const latest = analyses[0];
    const handle = eligibility?.instagram_handle;
    const needsProfile = eligibility && PROFILE_REASONS.includes(eligibility.reason);

    return (
        <Page>
            <ToastContainer toasts={toasts} />

            <Header>
                <HeaderTitle>
                    <HeaderIcon><ScanSearch size={18} color={panelAccent(colors.secondaryPurple)} /></HeaderIcon>
                    <div>
                        <Title>Análise do Instagram</Title>
                        <Subtitle>
                            {handle ? `@${handle}` : 'Diagnóstico do seu perfil cruzado com as suas vendas na Whizr'}
                        </Subtitle>
                    </div>
                </HeaderTitle>

                {needsProfile ? (
                    <Button variant='featured' category='squired' onClick={() => navigate(paths.dashboard.profile)}>
                        <UserCog color={colors.neutralLight} size={16} />
                        <Text color={colors.neutralLight} paddingBottom={0} marginLeft={'6px'}>Completar perfil</Text>
                    </Button>
                ) : (
                    <RequestAction>
                        <Button
                            variant={eligibility?.allowed ? 'featured' : 'disabled'}
                            category='squired'
                            onClick={() => eligibility?.allowed && setConfirmOpen(true)}
                        >
                            <Sparkles color={colors.neutralLight} size={16} />
                            <Text color={colors.neutralLight} paddingBottom={0} marginLeft={'6px'}>
                                {analyses.length ? 'Pedir nova análise' : 'Pedir análise'}
                            </Text>
                        </Button>
                        {eligibility?.reason === 'monthly_limit' && (
                            <Hint>Próxima disponível em {formatDate(eligibility.next_available_at)}</Hint>
                        )}
                    </RequestAction>
                )}
            </Header>

            <StatusBanner latest={latest} eligibility={eligibility} />

            {analysis && !loadingAnalysis && (
                <>
                    <TabsWrap>
                        <Tabs key={analysis.id} tabs={TABS} onChangeTab={setTab} />
                    </TabsWrap>

                    {tab === 'overview' && <OverviewTab analysis={analysis} />}
                    {tab === 'who' && <ProfileTab analysis={analysis} pillarsByTab={pillarsByTab('who')} toast={toast} />}
                    {tab === 'niche' && <MarketTab analysis={analysis} />}
                    {tab === 'community' && <AudienceTab analysis={analysis} pillarsByTab={pillarsByTab('community')} />}
                    {tab === 'sales' && <SalesTab analysis={analysis} pillarsByTab={pillarsByTab('sales')} />}
                    {tab === 'plan' && <PlanTab analysis={analysis} />}
                    {tab === 'history' && <HistoryTab analyses={analyses} selectedId={selectedId} onSelect={selectFromHistory} />}
                </>
            )}

            {loadingAnalysis && <Centered><Loader /></Centered>}

            {!selectedId && !latest && <Intro needsProfile={needsProfile} message={eligibility?.message} />}

            <ConfirmDialog
                isOpen={confirmOpen}
                title='Pedir análise do Instagram'
                message={`Vamos analisar o perfil @${handle} e cruzar com as vendas dos seus últimos eventos. A análise fica pronta em até alguns dias, e você pode pedir uma por mês.`}
                confirmLabel='Pedir análise'
                loading={requesting}
                onConfirm={requestAnalysis}
                onCancel={() => setConfirmOpen(false)}
            />
        </Page>
    );
}

/** Pedido em andamento ou recusado: aparece acima da última análise concluída. */
function StatusBanner({ latest, eligibility }) {
    if (!latest || latest.status === 'completed') {
        return null;
    }

    if (latest.status === 'rejected') {
        return (
            <Banner $tone='warning'>
                <AlertCircle size={18} />
                <div>
                    <BannerTitle>Não conseguimos analisar o @{latest.instagram_handle}</BannerTitle>
                    <Muted>
                        {latest.rejection_reason} {eligibility?.allowed && 'Esse pedido não conta no limite do mês: corrija e peça de novo.'}
                    </Muted>
                </div>
            </Banner>
        );
    }

    return (
        <Banner $tone='info'>
            <Clock size={18} />
            <div>
                <BannerTitle>
                    {latest.status === 'pending' ? 'Sua análise está na fila' : 'Estamos analisando o seu perfil'}
                </BannerTitle>
                <Muted>Pedido feito em {formatDate(latest.created_at)}. Avisamos por e-mail e aqui no painel quando ficar pronta.</Muted>
            </div>
        </Banner>
    );
}

function Intro({ needsProfile, message }) {
    const items = [
        ['Notas de 0 a 100', 'Bio, visual, conteúdo, engajamento e vendas, pelos mesmos critérios todo mês.'],
        ['Cruzamento com as suas vendas', 'Vemos se os seus posts movem a venda dos ingressos na Whizr.'],
        ['Plano de ação', 'O que mudar primeiro, com o que você já tem.'],
    ];

    return (
        <IntroCard>
            <IntroTitle>Descubra o que o seu Instagram faz pelas suas vendas</IntroTitle>
            {needsProfile && <Muted style={{ marginBottom: 12 }}>{message}</Muted>}
            <IntroList>
                {items.map(([title, text]) => (
                    <li key={title}>
                        <strong>{title}</strong>
                        <Muted>{text}</Muted>
                    </li>
                ))}
            </IntroList>
        </IntroCard>
    );
}

const Page = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const Centered = styled.div`
    min-height: 50vh;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 18px;

    @media screen and (min-width: 900px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;

        & button {
            width: auto;
        }
    }
`;

const HeaderTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const HeaderIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
`;

const Title = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Subtitle = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const RequestAction = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
`;

const Hint = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const TabsWrap = styled.div`
    margin-bottom: 22px;
`;

const Banner = styled(Card)`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 20px;
    background: ${({ $tone }) => ($tone === 'warning' ? colors.warning : colors.neutralBackground)};
    border-color: transparent;

    svg {
        flex-shrink: 0;
        margin-top: 2px;
        color: ${({ $tone }) => ($tone === 'warning' ? colors.goldDark : colors.primary)};
    }
`;

const BannerTitle = styled.p`
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const IntroCard = styled(Card)`
    max-width: 640px;
`;

const IntroTitle = styled.h4`
    margin: 0 0 12px;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const IntroList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;

    li {
        padding-left: 14px;
        border-left: 2px solid ${panelAccent(colors.secondaryPurple)};
    }

    strong {
        display: block;
        font-size: 14px;
        color: ${colors.nightDark};
    }
`;
